import { Fragment, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { useMutation } from '@apollo/client';
import { css } from '@emotion/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Button,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Theme,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import logger from 'logger';
import { useRouter } from 'next/router';
import PasswordValidation from 'src/components/SignupAndLogin/PasswordValidation';
import {
  REQUEST_RESET_PASSWORD,
  RESET_PASSWORD,
} from 'src/graphql-operations/auth-service/auth.mutation';
import {
  RequestPasswordResetMutation,
  RequestPasswordResetMutationVariables,
  ResetPasswordMutation,
  ResetPasswordMutationVariables,
} from 'src/ts/@types/generated/graphql/ecomm-auth-service/graphql';
import { ClientNames } from 'src/utils/apolloClient';
import { RegexPatterns } from 'src/utils/constants';
import { object, ref, string, InferType } from 'yup';

import MatchingPasswordValidation from './MatchingPasswordValidation';

const Form: React.FC<FormProps> = ({ isForgotPassword }) => {
  return isForgotPassword ? <ForgotPasswordForm /> : <ResetPasswordForm />;
};
const ForgotPasswordForm: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const router = useRouter();
  const theme = useTheme();
  const {
    control,
    handleSubmit,
    formState: { isValid },
    getValues,
  } = useForm({
    resolver: yupResolver(forgotPasswordSchema),
    mode: 'onChange',
  });
  const isSmallScreen = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('sm'),
  );
  const [requestPasswordReseMutation] = useMutation<
    RequestPasswordResetMutation,
    RequestPasswordResetMutationVariables
  >(REQUEST_RESET_PASSWORD, {
    context: { clientName: ClientNames.AUTH_SERVICE_GRAPH },
  });

  const onSubmit = async () => {
    setIsLoading(true);
    const values = getValues();
    try {
      await requestPasswordReseMutation({
        variables: { email: values.email },
      }).then(({ data }) => {
        if (data?.requestPasswordReset) {
          setIsLoading(false);
          router.push({
            pathname: '/login',
            query: {
              comesFrom: 'forgot-password',
            },
          });
        } else {
          //UX that mocks rails behaviour when there's an error, we don't give feedback here for security reasons
          window.location.reload();
        }
      });
    } catch (err: any) {
      setIsLoading(false);
      logger.error({
        msg: 'Falied to request reset password email',
        noticable: err,
      });
    }
  };

  return (
    <FormControl fullWidth error={!isValid}>
      <Grid
        container
        spacing={4}
        columns={12}
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
      >
        <Grid css={{ marginTop: '0.5rem' }}>
          <Controller
            name="email"
            control={control}
            defaultValue=""
            render={({ field: { onChange, value } }) => (
              <TextField
                color="primary"
                css={isSmallScreen ? mobileInputStyles : largeInputStyles}
                placeholder="kale@example.com"
                label="Email"
                required
                value={value}
                onChange={onChange}
              />
            )}
          />
        </Grid>
        <Grid css={{ marginTop: '1rem' }}>
          <Button
            type="submit"
            size="large"
            variant={isLoading ? 'outlined' : 'contained'}
            disabled={!isValid || isLoading}
            css={isSmallScreen ? mobileInputStyles : largeInputStyles}
            onClick={handleSubmit(onSubmit)}
          >
            {isLoading && (
              <CircularProgress
                css={{ marginRight: '0.25rem', color: theme.palette.grey[300] }}
                size={20}
              />
            )}
            SUBMIT
          </Button>
        </Grid>
      </Grid>
    </FormControl>
  );
};
const ResetPasswordForm: React.FC = () => {
  const router = useRouter();
  const isSmallScreen = useMediaQuery<Theme>((theme) =>
    theme.breakpoints.down('sm'),
  );
  const [password, setPassword] = useState<string>('');
  const [isPasswordShown, setIsPasswordShown] = useState<boolean>(false);
  const [passwordConfirmation, setPasswordConfirmation] = useState<string>('');
  const [isPasswordConfShown, setIsPasswordConfShown] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const theme = useTheme();
  const {
    control,
    handleSubmit,
    formState: { isValid },
    getValues,
    setError,
  } = useForm({
    resolver: yupResolver(resetPasswordSchema),
    mode: 'onChange',
  });

  const [resetPasswordMutation2] = useMutation<
    ResetPasswordMutation,
    ResetPasswordMutationVariables
  >(RESET_PASSWORD, {
    context: { clientName: ClientNames.AUTH_SERVICE_GRAPH },
  });

  const onSubmit = async () => {
    setIsLoading(true);
    const values = getValues();
    try {
      const resetToken = router.query.reset_password_token;
      if (!resetToken || typeof resetToken !== 'string') {
        throw new Error(
          'Attempted to reset password with no reset token present in query params',
        );
      }
      await resetPasswordMutation2({
        variables: {
          password: values.password as string,
          passwordConfirmation: values.passwordConfirmation as string,
          resetToken,
        },
      }).then(({ data }) => {
        setIsLoading(false);
        if (data?.resetPassword?.__typename === 'UpdatePasswordSuccess') {
          router.push({
            pathname: '/login',
            query: {
              comesFrom: 'reset-password',
            },
          });
        } else if (data?.resetPassword?.__typename === 'UpdatePasswordError') {
          const { validation } = data.resetPassword;
          //sets error messages if any, so they can be displayed on individual fields
          Object.entries(validation!).forEach(([key, value]) => {
            if (key && value) {
              setError(key as keyof ResetPasswordSchema, {
                message: value as string,
              });
            }
          });
          //@todo: should we do something if token is invalid? Maybe not show error but redirect him
        }
      });
    } catch (err: any) {
      setIsLoading(false);
      logger.error({
        msg: 'Falied to request reset password email',
        noticable: err,
      });
    }
  };

  return (
    <FormControl fullWidth error={!isValid}>
      <Grid
        container
        spacing={2}
        columns={12}
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
      >
        <Fragment>
          <Grid css={{ marginTop: '0.5rem' }}>
            <Controller
              name="password"
              control={control}
              defaultValue=""
              render={({ field: { onChange }, formState: { errors } }) => (
                <TextField
                  color="primary"
                  css={isSmallScreen ? mobileInputStyles : largeInputStyles}
                  label="New password"
                  required
                  value={password}
                  type={isPasswordShown ? 'text' : 'password'}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    onChange(e.target.value);
                  }}
                  error={!!errors?.password}
                  helperText={
                    errors?.password?.type === 'custom'
                      ? errors?.password?.message
                      : null
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setIsPasswordShown(!isPasswordShown)}
                          edge="end"
                        >
                          {isPasswordShown ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
            <div css={{ marginTop: '0.5rem' }}>
              <PasswordValidation password={password} />
            </div>
          </Grid>
          <Grid css={{ marginTop: '0.5rem' }}>
            <Controller
              name="passwordConfirmation"
              control={control}
              defaultValue=""
              render={({ field: { onChange } }) => (
                <TextField
                  color="primary"
                  css={isSmallScreen ? mobileInputStyles : largeInputStyles}
                  label="Confirm new password"
                  required
                  value={passwordConfirmation}
                  type={isPasswordConfShown ? 'text' : 'password'}
                  onChange={(e) => {
                    setPasswordConfirmation(e.target.value);
                    onChange(e.target.value);
                  }}
                  error={
                    !!password &&
                    !!passwordConfirmation &&
                    password !== passwordConfirmation
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() =>
                            setIsPasswordConfShown(!isPasswordConfShown)
                          }
                          edge="end"
                        >
                          {isPasswordConfShown ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          {passwordConfirmation && (
            <Grid
              display="inline-flex"
              alignItems="center"
              justifyContent="flex-start"
              width="100%"
              css={{ marginTop: '-0.5rem' }}
            >
              <MatchingPasswordValidation
                password={password}
                passwordConfirmation={passwordConfirmation}
              />
            </Grid>
          )}
        </Fragment>
        <Grid>
          <Button
            type="submit"
            size="large"
            variant={isLoading ? 'outlined' : 'contained'}
            disabled={!isValid || isLoading}
            css={isSmallScreen ? mobileInputStyles : largeInputStyles}
            onClick={handleSubmit(onSubmit)}
          >
            {isLoading && (
              <CircularProgress
                css={{ marginRight: '0.25rem', color: theme.palette.grey[300] }}
                size={20}
              />
            )}
            CHANGE MY PASSWORD
          </Button>
        </Grid>
      </Grid>
    </FormControl>
  );
};

export default Form;

const largeInputStyles = css({
  width: '30rem',
});

const mobileInputStyles = css({
  width: '20rem',
});

interface FormProps {
  isForgotPassword?: boolean | null;
}

const forgotPasswordSchema = object({
  email: string().required().email().defined(),
});

const resetPasswordSchema = object({
  password: string()
    .required(' ')
    .matches(RegexPatterns.IS_BIGGER_THAN_SIX_CHARS, ' ')
    .matches(RegexPatterns.HAS_UPPERCASE_LETTER, ' ')
    .matches(RegexPatterns.HAS_NUMBER, ' ')
    .matches(RegexPatterns.HAS_SPECIAL_CHAR, ' ')
    .defined(),
  passwordConfirmation: string()
    .required()
    .oneOf([ref('password')]),
});
type ResetPasswordSchema = InferType<typeof resetPasswordSchema>;
